/**
 * Where an instrument trades, and what that costs.
 *
 * ## Why this replaced an enum
 *
 * `Exchange` was `'CME' | 'CBOT' | 'NYMEX' | 'COMEX'` — a closed union of the
 * four CME Group venues, which was exactly right for a futures product and is
 * the first thing that breaks when stocks, crypto and forex arrive. Widening
 * the union would have meant editing a type every time a venue was added, which
 * contradicts the rule that the instrument universe is data rather than code.
 *
 * So a venue is a row. `VenueId` is a plain string, and every instrument's
 * venue is checked against this table at module load — the same bargain the
 * tick identity makes: fail the process at startup rather than carry a wrong
 * value into a P&L six months later.
 *
 * ## The field that matters most
 *
 * `licensed` is not descriptive metadata. It is the fact the whole data tier
 * turns on, and it does not split the way people expect:
 *
 *   > Exchange-traded data is licensed per subscriber and costs money.
 *   > OTC data is not and does not.
 *
 * Futures and US equities are on the SAME side of that line — both are
 * per-subscriber, both have a professional/non-professional distinction. Crypto
 * and spot forex are on the other, because no venue owns the price: crypto
 * exchanges publish public feeds, and spot FX has no consolidated tape at all.
 *
 * Reading `licensed` off a row is what lets Phase 45 gate live data without a
 * hard-coded list of "the expensive ones" drifting out of step with the table.
 *
 * @see docs/26-multi-asset-expansion.md — "Market data: the honest tier"
 * @see docs/04-market-data-and-licensing.md
 */

import { SESSION_TEMPLATES } from './sessions.js';

/** A venue's identifier. Free-form by design; validated against `VENUES`. */
export type VenueId = string;

export type VenueKind =
  /** A regulated exchange that owns and licenses its price. */
  | 'exchange'
  /** No central venue. Forex spot: the price comes from an LP or an aggregate. */
  | 'otc'
  /** A crypto exchange. Publishes a public feed nobody licenses. */
  | 'crypto'
  /** The broker invented the price. CFDs. Sourceable from that broker alone. */
  | 'broker';

export interface Venue {
  readonly id: VenueId;
  readonly name: string;
  readonly kind: VenueKind;
  /**
   * True when displaying this venue's real-time data to a user is a licensed,
   * per-subscriber act. Drives the delayed-vs-live tier; never a display hint.
   */
  readonly licensed: boolean;
  /**
   * What a non-entitled viewer sees, in milliseconds.
   *
   * CME's convention is 10 minutes and the US equity convention is 15. They are
   * not interchangeable, and a badge that rounds them together is telling the
   * user something false about how stale their chart is.
   */
  readonly defaultDelayMs: number;
  /** Trading hours and — critically — where the trading day boundary sits. */
  readonly sessionTemplateId: string;
}

const MIN = 60_000;

/**
 * The venue table.
 *
 * Adding a venue is a row here and nothing else. Note that the two `licensed`
 * venues carry a real delay and the three unlicensed ones carry zero — that
 * correlation is the licensing fact, not a coincidence to be tidied away.
 */
export const VENUES: Readonly<Record<VenueId, Venue>> = Object.freeze({
  // --- CME Group. Licensed, ~$9/mo non-pro for the Globex Level 1 bundle. ---
  CME: { id: 'CME', name: 'Chicago Mercantile Exchange', kind: 'exchange', licensed: true, defaultDelayMs: 10 * MIN, sessionTemplateId: 'cme_equity_index' },
  CBOT: { id: 'CBOT', name: 'Chicago Board of Trade', kind: 'exchange', licensed: true, defaultDelayMs: 10 * MIN, sessionTemplateId: 'cme_equity_index' },
  NYMEX: { id: 'NYMEX', name: 'New York Mercantile Exchange', kind: 'exchange', licensed: true, defaultDelayMs: 10 * MIN, sessionTemplateId: 'cme_energy_metals' },
  COMEX: { id: 'COMEX', name: 'Commodity Exchange', kind: 'exchange', licensed: true, defaultDelayMs: 10 * MIN, sessionTemplateId: 'cme_energy_metals' },

  /*
    ICE Futures U.S. — the softs, and a separate licence entirely.

    Not a CME Group venue, so a CME market-data agreement does not cover it: a
    trader with live ES has DELAYED coffee unless ICE is separately entitled.
    `licensed: true` is what makes the tier honest about that rather than
    quietly showing an ICE price the subscription does not pay for.
  */
  ICEUS: { id: 'ICEUS', name: 'ICE Futures U.S.', kind: 'exchange', licensed: true, defaultDelayMs: 10 * MIN, sessionTemplateId: 'ice_softs' },

  /* Cboe Futures Exchange — VX. Its own licence, same reasoning as ICE. */
  CFE: { id: 'CFE', name: 'Cboe Futures Exchange', kind: 'exchange', licensed: true, defaultDelayMs: 15 * MIN, sessionTemplateId: 'cfe_volatility' },

  // --- US equities. CTA/UTP plans: the same shape of problem as CME. ---
  NASDAQ: { id: 'NASDAQ', name: 'Nasdaq', kind: 'exchange', licensed: true, defaultDelayMs: 15 * MIN, sessionTemplateId: 'us_equity' },
  NYSE: { id: 'NYSE', name: 'New York Stock Exchange', kind: 'exchange', licensed: true, defaultDelayMs: 15 * MIN, sessionTemplateId: 'us_equity' },
  ARCA: { id: 'ARCA', name: 'NYSE Arca', kind: 'exchange', licensed: true, defaultDelayMs: 15 * MIN, sessionTemplateId: 'us_equity' },

  // --- Crypto. Public feeds, 24/7, nothing to license. ---
  BINANCE: { id: 'BINANCE', name: 'Binance', kind: 'crypto', licensed: false, defaultDelayMs: 0, sessionTemplateId: 'always_open' },
  COINBASE: { id: 'COINBASE', name: 'Coinbase', kind: 'crypto', licensed: false, defaultDelayMs: 0, sessionTemplateId: 'always_open' },
  KRAKEN: { id: 'KRAKEN', name: 'Kraken', kind: 'crypto', licensed: false, defaultDelayMs: 0, sessionTemplateId: 'always_open' },

  // --- Spot forex. No consolidated tape exists, so there is no owner to pay. ---
  FX: { id: 'FX', name: 'Spot FX (interbank aggregate)', kind: 'otc', licensed: false, defaultDelayMs: 0, sessionTemplateId: 'fx_spot' },

  // --- CFDs. The price is the broker's own invention. ---
  CFD: { id: 'CFD', name: 'Broker CFD', kind: 'broker', licensed: false, defaultDelayMs: 0, sessionTemplateId: 'fx_spot' },
});

/**
 * Every venue's session template must resolve.
 *
 * Asserted at module load for the same reason the instrument guards are: an
 * unresolvable id is invisible until a request asks for a timezone, and then it
 * is a 500 on a route that lists instruments rather than an error at boot.
 */
for (const v of Object.values(VENUES)) {
  if (!Object.hasOwn(SESSION_TEMPLATES, v.sessionTemplateId)) {
    throw new Error(
      `Venue ${v.id}: unknown session template "${v.sessionTemplateId}". `
      + `Add it to SESSION_TEMPLATES.`,
    );
  }
}

export function venue(id: VenueId): Venue {
  const v = VENUES[id];
  if (!v) throw new Error(`Unknown venue "${id}". Add a row to VENUES.`);
  return v;
}

export function isKnownVenue(id: string): boolean {
  return Object.hasOwn(VENUES, id);
}

/**
 * Whether real-time display of this venue's data must be paid for per subscriber.
 *
 * The one question the data tier asks. Deliberately a function of the venue and
 * nothing else — not the asset class, which is a different axis and gets this
 * wrong (a metal future and spot gold are the same asset class and opposite
 * sides of this line).
 */
export function requiresEntitlement(id: VenueId): boolean {
  return venue(id).licensed;
}

/** What a viewer without an entitlement sees. Zero where nothing is licensed. */
export function defaultDelayMsFor(id: VenueId): number {
  return venue(id).defaultDelayMs;
}
